import {Controller} from '@hotwired/stimulus';
import {createConsumer} from "@rails/actioncable"

export default class extends Controller {
    static targets = ["countdown"];
    static values = {
        endsAt: String,
        expiresAt: String,
        poolId: Number,
        url: String
    };

    connect() {
        console.log(`AuctionTimerController connected for pool ${this.poolIdValue}`);
        this.ensureFrameSrc();
        this.startTimer();
        this.setupSubscription();
    }

    ensureFrameSrc() {
        if (this.hasUrlValue && !this.element.src) {
            this.element.setAttribute("src", this.urlValue);
            this.element.setAttribute("loading", "lazy");
        }
    }

    disconnect() {
        this.stopTimer();
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
        }
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.consumer) {
            this.consumer.disconnect();
        }
    }

    startTimer() {
        this.stopTimer();
        this.updateCountdown();
        this.checkedServerTimerCount = 0;
        this.timer = setInterval(() => this.updateCountdown(), 1000);
    }

    stopTimer() {
        if (this.timer) {
            clearInterval(this.timer);
        }
    }

    updateCountdown() {
        const dateStr = this.endsAtValue || this.expiresAtValue;
        if (!dateStr) return;

        const targetDate = new Date(dateStr);
        const now = new Date();
        const diff = Math.max(0, Math.floor((targetDate - now) / 1000));

        if (this.hasCountdownTarget) {
            this.countdownTarget.textContent = this.formatDuration(diff);
        }

        if (diff <= 0) {
            this.stopTimer();
            this.checkServerTimer();
        }
    }

    async checkServerTimer() {
        if (this.checkingServerTimer) return;
        this.checkingServerTimer = true;

        console.log("Timer expired, checking server...");

        try {
            const response = await fetch(`/pools/${this.poolIdValue}/auction/timers/check`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
                }
            });

            if (!response.ok) {
                console.error("Failed to check server", response.statusText);
            }

            const payload = await response.json();
            console.log("status", payload.status, payload);

            if (payload.status !== "not_expired") {
                console.log("checking again ....");
                this.checkedServerTimerCount += 1
                this.checkingServerTimer = false;
                if (this.checkedServerTimerCount < 5) {
                    setTimeout(() => this.checkServerTimer(), 1000);
                }
            } else if (payload.status === "expired") {
                this.reloadPage();
            }

        } catch (error) {
            console.error("Error poking server:", error);
        } finally {
            // Re-enable check after a delay if status hasn't changed via ActionCable
            console.log("finally")
            setTimeout(() => {
                console.log("resetting");
                this.checkingServerTimer = false;
            }, 5000);
        }
    }

    reloadPage() {
        Turbo.visit(window.location.pathname, {action: "replace"});
    }

    formatDuration(seconds) {
        if (seconds <= 0) return "0s";

        const parts = [];
        const days = Math.floor(seconds / 86400);
        if (days > 0) {
            parts.push(`${days}d`);
            seconds %= 86400;
        }

        const hours = Math.floor(seconds / 3600);
        if (hours > 0 || days > 0) {
            parts.push(`${hours}h`);
            seconds %= 3600;
        }

        const minutes = Math.floor(seconds / 60);
        if (minutes > 0 || hours > 0 || days > 0) {
            parts.push(`${minutes}m`);
            seconds %= 60;
        }

        parts.push(`${seconds}s`);
        return parts.join(" ");
    }

    setupSubscription() {
        if (!this.poolIdValue) return;

        this.consumer = createConsumer();
        this.subscription = this.consumer.subscriptions.create(
            {channel: "AuctionChannel", pool_id: this.poolIdValue},
            {
                received: (data) => {
                    console.log("Auction update received:", data);
                    this.handleUpdate(data);
                }
            }
        );
    }

    handleUpdate(data) {
        if (data.type === "timer_update") {
            if (this.hasEndsAtValue) {
                this.endsAtValue = data.timer.ends_at;
            } else {
                this.expiresAtValue = data.timer.ends_at;
            }
            this.startTimer();
            this.refreshAuctionUI();
        } else if (data.type === "bid_submitted") {
            // Small delay to allow DB commit to finish before frame reloads
            // Debounce multiple bids
            if (this.refreshTimeout) {
                clearTimeout(this.refreshTimeout);
            }
            this.refreshTimeout = setTimeout(() => this.refreshAuctionUI(), 500);
        } else if (data.type === "status_changed" || data.type === "auction_finished") {
            window.location.reload();
        }
    }

    refreshAuctionUI() {
        console.log("Refreshing Auction UI...");
        const poolId = this.poolIdValue;

        const frameIds = [
            `auction-window-${poolId}`,
            `auction-matrix-frame-${poolId}`,
            `auction-bid-table-${poolId}`,
            `pool-payout-widget-${poolId}`,
            `auction-bid-history-${poolId}`
        ];

        frameIds.forEach(id => {
            const frame = document.getElementById(id);
            if (frame) {
                this.reloadFrame(frame);
            }
        });
    }

    reloadFrame(frame) {
        if (!frame) return;

        // If the frame has no src, set it to the pool URL to enable reloading
        // Setting src automatically triggers a load in Turbo
        if (!frame.src && this.hasUrlValue) {
            console.log(`Setting src for frame ${frame.id} to ${this.urlValue}`);
            frame.src = this.urlValue;
            return;
        }

        try {
            console.log(`Triggering reload for frame ${frame.id}`);
            const result = frame.reload();
            if (result instanceof Promise) {
                result.catch(e => {
                    if (e && e.name !== 'AbortError') {
                        console.error(`Error reloading frame ${frame.id}:`, e);
                    }
                });
            }
        } catch (e) {
            if (e && e.name !== 'AbortError') {
                console.error(`Error triggering reload for frame ${frame.id}:`, e);
            }
        }
    }
}
